import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { pdf, PDFViewer  } from '@react-pdf/renderer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { getBas264Image } from '../../../controllers/V1/generalDataController';

import PdfTemplates from '../../../components/PdfTemplates';

// Helper function to detect image format and format base64 data URI
const formatImageDataUri = (base64Image, imageUrl = '') => {
  if (!base64Image || typeof base64Image !== 'string') return base64Image;
  
  // If already formatted as data URI, return as is
  if (base64Image.startsWith('data:')) return base64Image;
  
  // If it's a URL, return as is
  if (base64Image.startsWith('http')) return base64Image;
  
  // Detect format from URL extension
  let imageType = 'jpeg'; // default
  if (imageUrl) {
    const urlLower = imageUrl.toLowerCase();
    if (urlLower.includes('.png')) {
      imageType = 'png';
    } else if (urlLower.includes('.jpg') || urlLower.includes('.jpeg')) {
      imageType = 'jpeg';
    }
  }
  
  // Try to detect from base64 data (magic bytes)
  // PNG: starts with iVBORw0KGgo
  // JPEG: starts with /9j/
  if (base64Image.startsWith('iVBORw0KGgo')) {
    imageType = 'png';
  } else if (base64Image.startsWith('/9j/')) {
    imageType = 'jpeg';
  }
  
  return `data:image/${imageType};base64,${base64Image}`;
};

// Helper function to process a single image field
const processImageField = async (imageData, fieldName) => {
  if (!imageData) return null;
  
  try {
    // Extract image URL - handle both string and object formats
    const imageUrl = typeof imageData === 'string' 
      ? imageData 
      : imageData[fieldName] || imageData;
    
    if (!imageUrl) return null;
    
    // Fetch base64 image from API
    const base64Image = await getBas264Image({image_url: imageUrl});
    
    // Check if API returned an error
    if (base64Image && typeof base64Image === 'object' && base64Image.status === false) {
      console.error(`Failed to fetch ${fieldName} image:`, base64Image.message);
      return null;
    }
    
    // Format base64 as data URI with detected image type
    if (base64Image && typeof base64Image === 'string') {
      const formattedImage = formatImageDataUri(base64Image, imageUrl);
      
      return {
        ...(typeof imageData === 'object' ? imageData : {}),
        [fieldName]: formattedImage,
      };
    }
    
    return null;
  } catch (error) {
    console.error(`Error processing ${fieldName} image:`, error);
    return null;
  }
};

// Export processFormDataImages for reuse
export const processFormDataImages = async (formData) => {
  if (!formData) return formData;
  
  const processedData = { ...formData };
  
  // Process all image fields using the helper function
  if (processedData.doc) {
    const processedDoc = await processImageField(processedData.doc, 'doc');
    if (processedDoc) {
      processedData.doc = processedDoc;
    }
  }
  
  if (processedData.order_slip) {
    const processedOrderSlip = await processImageField(processedData.order_slip, 'order_slip');
    if (processedOrderSlip) {
      processedData.order_slip = processedOrderSlip;
    }
  }
  
  if (processedData.redeem_slip) {
    const processedRedeemSlip = await processImageField(processedData.redeem_slip, 'redeem_slip');
    if (processedRedeemSlip) {
      processedData.redeem_slip = processedRedeemSlip;
    }
  }
  
  if (processedData.other_attachment) {
    const processedOtherAttachment = await processImageField(processedData.other_attachment, 'other_attachment');
    if (processedOtherAttachment) {
      processedData.other_attachment = processedOtherAttachment;
    }
  }

  if (processedData.signature) {
    const processedsignature = await processImageField(processedData.signature, 'signature');
    if (processedsignature) {
      processedData.signature = processedsignature;
    }
  }

  if (processedData.images) {
    const processedImages = await processImageField(processedData.images, 'images');
    if (processedImages) {
      processedData.images = processedImages;
    }
  }

  if (processedData.images2) {
    const processedImages2 = await processImageField(processedData.images2, 'images2');
    if (processedImages2) {
      processedData.images2 = processedImages2;
    }
  }
  
  return processedData;
};

const PreviewPDF = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const voucherData = location?.state;
  const [processedData, setProcessedData] = useState(null);


  useEffect(() => {
    processFormDataImages(voucherData).then((data) => {
      setProcessedData(data);
    });
  }, [voucherData]);
  
  useEffect(() => {
    // Auto-download PDF after images are processed and loaded
    const generateAndDownloadPDF = async () => {
      // Wait for processedData to be available (images are loaded)
      if (processedData) {
        try {
          const doc = <PdfTemplates voucherData={processedData || voucherData} />;
          const asPdf = pdf(doc);
          const blob = await asPdf.toBlob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Winning Voucher ${processedData?.order_id || ''} - ${moment(new Date()).format('DD-MM-YYYY')}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error generating PDF:', error);
        }
      }
    };

    generateAndDownloadPDF();
  }, [processedData]);

  if (!voucherData) {
    return <div>No voucher data available</div>;
  }

  return (
    <div style={{ height: "90vh", textAlign: 'center' }}>
        <div className="invoice-container" style={{
            width: '100%',
            height: '100vh',
            margin: 'auto',
            backgroundColor: '#fff',
            padding: '20px',
            border: '1px solid #ccc',
            borderRadius: '10px',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
            fontSize: '13px',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <div style={{ marginBottom: '10px', textAlign: 'left' }}>
                <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => navigate(-1)}
                >
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                </button>
            </div>
            <PDFViewer style={{
                width: '100%',
                height: 'calc(100vh - 100px)',
                minHeight: '600px',
            }}>
                <PdfTemplates voucherData={processedData || voucherData} />      
            </PDFViewer>
        </div>
    </div>
  );
};

export default PreviewPDF;
